import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Briefcase,
  Settings,
  Users,
  Calendar,
  Award,
  User,
  HelpCircle,
  Mail,
} from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
  icon: React.ReactNode;
  cta: string;
}

export default function TrainingFAQ() {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
    {},
  );

  const faqs: FAQItem[] = [
    {
      icon: <Briefcase className="h-8 w-8 text-gray-900" />,
      question:
        "What types of AI training does One80Labs offer for businesses in Omaha and the Midwest?",
      answer:
        "We offer practical AI training built for real-world results. From AI strategy to automation and prompt engineering, our courses help businesses in Omaha, Council Bluffs, and throughout the Midwest implement tools that save time and improve efficiency. Whether you're new to AI or ready to expand your capabilities, we've got a course for you.",
      cta: "Partner with a trainer who gives your business the focus it deserves.",
    },
    {
      icon: <Settings className="h-8 w-8 text-gray-900" />,
      question:
        "Can we customize the AI training to fit our team's goals and industry?",
      answer:
        "Yes—and customization is at the heart of what we do. We tailor every course to meet your organization's goals, tools, and skill levels. Whether you're in healthcare, logistics, non-profit, or tech, we'll build a training plan that aligns with your outcomes. We've customized AI classes for teams across Nebraska and the broader Midwest region.",
      cta: "Get a training program designed specifically for your industry needs.",
    },
    {
      icon: <Calendar className="h-8 w-8 text-gray-900" />,
      question:
        "Do you offer in-person AI workshops or virtual training sessions?",
      answer:
        "We offer in-person, virtual, and hybrid AI training. Our in-person sessions are perfect for companies in Omaha, Council Bluffs, Lincoln, and across the Midwest, while our virtual training lets you join from anywhere. We work with you to choose the format that works best for your team's needs.",
      cta: "Choose the training format that works best for your team.",
    },
    {
      icon: <Users className="h-8 w-8 text-gray-900" />,
      question: "Who can attend One80Labs' AI training sessions?",
      answer:
        "Anyone ready to build or advance their AI skills. We train individuals, teams, and departments—no technical background required. Whether you're a business analyst, marketing lead, IT manager, or just curious about AI, we'll meet you where you are and help you level up fast.",
      cta: "Join our inclusive training sessions designed for all skill levels.",
    },
    {
      icon: <Award className="h-8 w-8 text-gray-900" />,
      question:
        "What makes One80Labs' AI training different from other providers?",
      answer:
        "We focus on outcomes, not just content. Our trainers are working AI consultants—not just instructors. We build tools for businesses every day, and we bring that hands-on experience into every session. Plus, we keep class sizes small, and every session is interactive, practical, and customized for your organization's success.",
      cta: "Experience training from active AI practitioners, not just instructors.",
    },
    {
      icon: <User className="h-8 w-8 text-gray-900" />,
      question:
        "Do you offer AI training for individuals looking to boost their career skills?",
      answer:
        "Absolutely—our AI courses are perfect for professionals across the Midwest. Whether you're in Omaha or anywhere else, we offer flexible learning options that give you real, job-ready AI skills. Learn how to automate tasks, build better prompts, and use AI to stand out in your career. No coding experience needed.",
      cta: "Boost your career with practical AI skills that employers value.",
    },
    {
      icon: <HelpCircle className="h-8 w-8 text-gray-900" />,
      question:
        "Can One80Labs help our business build AI agents or automated solutions?",
      answer:
        "Yes—we build custom AI agents and automation tools for businesses across the Midwest. Whether you need a chatbot, task automation, or an AI-powered internal tool, our team can help design, build, and integrate a solution tailored to your workflow. We've helped clients in Omaha, Lincoln, and Council Bluffs save time and cut costs with smart automation.",
      cta: "Transform your business with custom AI solutions built for your needs.",
    },
    {
      icon: <Mail className="h-8 w-8 text-gray-900" />,
      question: "How do we get started with training or AI development?",
      answer:
        "It's easy—just reach out. Email us at hello@one80labs.com and we'll schedule a quick call to learn more about your goals. From there, we'll send you a custom proposal for training, development, or both.",
      cta: "Take the first step toward AI transformation for your organization.",
    },
  ];

  const toggleItem = (index: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        AI Training – Frequently Asked Questions
        <span className="block text-base font-normal text-gray-600 mt-2">
          (Serving Omaha, Council Bluffs, and organizations across the Midwest)
        </span>
      </h2>
      <div
        className="space-y-4"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white transition-all duration-300 hover:shadow-md"
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
          >
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-gray-100 p-3 rounded-lg">{faq.icon}</div>
                <h3
                  className="text-lg font-semibold text-gray-900"
                  itemProp="name"
                >
                  {faq.question}
                </h3>
              </div>
              {expandedItems[index] ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            <div
              className={`px-6 overflow-hidden transition-all duration-300 ${
                expandedItems[index] ? "max-h-96 py-4" : "max-h-0"
              }`}
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
            >
              <p className="text-gray-600 mb-4" itemProp="text">
                {faq.answer}
              </p>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-[#F98B3D] mt-1 mr-2 flex-shrink-0" />
                <p className="text-[#F98B3D]">{faq.cta}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
